/**
 * خطاهای API (به‌ویژه ۴۲۲ Laravel): `getApiErrorMessage` پیام درست (اولین متن `errors`) را برمی‌گرداند؛
 * `applyApiValidationToForm` همان نقشه را با `form.setFields` زیر فیلدهای antd می‌گذارد.
 * در `catch` فرم: `if (e?.errorFields) return;` سپس `applyApiValidationToForm(...)` و `message.error(getApiErrorMessage(...))`.
 *
 * @param {unknown} val
 * @returns {string[]}
 */
import { AUDIO_UPLOAD_MAX_MB } from '../constants/audioUpload';
import { hideConferenceVendorName } from './conferenceVendorCopy';

function isPostTooLargeMessage(text) {
  return /POST data is too large|post_max_size|upload_max_filesize/i.test(String(text || ''));
}

function isTransportFailureMessage(text) {
  const raw = String(text || '').trim();
  return /^(network error|load failed|failed to fetch)$/i.test(raw)
    || /networkerror|the network connection was lost|err_network|internet connection appears to be offline/i.test(raw);
}

function transportFailureUserMessage() {
  return 'ارتباط با سرور قطع شد. اتصال اینترنت را بررسی کنید و دوباره تلاش کنید.';
}

function postTooLargeUserMessage() {
  return `حجم فایل صوتی بیشتر از حد مجاز است. حداکثر ${AUDIO_UPLOAD_MAX_MB} مگابایت انتخاب کنید.`;
}

function normalizeValidationMessages(val) {
  if (Array.isArray(val)) {
    return val.map((m) => String(m).trim()).filter(Boolean);
  }
  if (typeof val === 'string' && val.trim()) return [val.trim()];
  return [];
}

/**
 * نقشهٔ خطاهای ولیدیشن سرور (مثلاً Laravel 422) از پاسخ axios.
 *
 * @param {unknown} error
 * @returns {Record<string, string[]>|null}
 */
export function getApiValidationErrorMap(error) {
  const err = error && typeof error === 'object' ? error : {};
  const response = /** @type {{ response?: { data?: unknown } }} */ (err).response;
  const data = response?.data;
  if (!data || typeof data !== 'object') return null;
  const raw = /** @type {{ errors?: unknown }} */ (data).errors;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  /** @type {Record<string, string[]>} */
  const out = {};
  for (const [key, val] of Object.entries(raw)) {
    const msgs = normalizeValidationMessages(val);
    if (msgs.length) out[key] = msgs;
  }
  return Object.keys(out).length ? out : null;
}

/**
 * تبدیل کلید API (مثلاً `settings.theme`) به `name` فرم antd.
 *
 * @param {string} apiKey
 * @param {Record<string, import('antd').FormItemProps['name']>|undefined} nameMap
 */
export function resolveApiFieldToFormName(apiKey, nameMap) {
  if (nameMap && Object.prototype.hasOwnProperty.call(nameMap, apiKey)) {
    const mapped = nameMap[apiKey];
    if (Array.isArray(mapped)) return mapped;
    if (typeof mapped === 'number') return mapped;
    if (typeof mapped === 'string' && mapped.includes('.')) {
      return mapped.split('.').map((part) => (/^\d+$/.test(part) ? Number(part) : part));
    }
    return mapped;
  }
  if (apiKey.includes('.')) {
    return apiKey.split('.').map((part) => (/^\d+$/.test(part) ? Number(part) : part));
  }
  return apiKey;
}

/**
 * اعمال خطاهای ۴۲۲ زیر فیلدهای فرم antd (`setFields`).
 *
 * @param {{ setFields: (fields: { name: import('antd').FormItemProps['name']; errors: string[] }[]) => void }} form
 * @param {unknown} error
 * @param {Record<string, import('antd').FormItemProps['name']>|undefined} [nameMap]
 * @returns {boolean} اگر نقشهٔ errors از سرور اعمال شد true
 */
export function applyApiValidationToForm(form, error, nameMap) {
  if (!form || typeof form.setFields !== 'function') return false;
  const map = getApiValidationErrorMap(error);
  if (!map) return false;
  const fields = Object.entries(map).map(([key, messages]) => ({
    name: resolveApiFieldToFormName(key, nameMap),
    errors: messages,
  }));
  form.setFields(fields);
  return true;
}

/**
 * @param {unknown} error
 * @returns {number|null}
 */
export function getApiRetryAfterSeconds(error) {
  const err = error && typeof error === 'object' ? error : {};
  const response = /** @type {{ response?: { data?: unknown; headers?: Record<string, string> } }} */ (err).response;
  if (!response) return null;

  const data = response.data;
  if (data && typeof data === 'object') {
    const retryAfter = /** @type {{ retry_after?: unknown }} */ (data).retry_after;
    if (typeof retryAfter === 'number' && retryAfter > 0) {
      return Math.ceil(retryAfter);
    }
  }

  const headers = response.headers ?? {};
  const headerValue = headers['retry-after'] ?? headers['Retry-After'];
  const parsed = Number.parseInt(String(headerValue ?? ''), 10);

  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isApiNotFoundError(error) {
  const err = error && typeof error === 'object' ? error : {};
  const status = /** @type {{ response?: { status?: number | string; data?: unknown } }} */ (err).response?.status;
  if (status === 404 || status === '404') return true;

  const data = /** @type {{ response?: { data?: unknown } }} */ (err).response?.data;
  if (data && typeof data === 'object') {
    const msg = /** @type {{ message?: string }} */ (data).message;
    if (typeof msg === 'string' && /یافت نشد|not found/i.test(msg)) return true;
  }

  return false;
}

/**
 * استخراج پیام خطای قابل نمایش از پاسخ axios / Laravel validation.
 * اگر `errors` وجود داشته باشد، قبل از پیام کلی مثل «Validation failed» از متن فیلدها استفاده می‌شود.
 *
 * @param {unknown} error
 * @param {string} [fallback]
 * @returns {string}
 */
export function getApiErrorMessage(error, fallback = 'خطایی رخ داد. دوباره تلاش کنید.') {
  return hideConferenceVendorName(resolveApiErrorMessage(error, fallback));
}

function resolveApiErrorMessage(error, fallback = 'خطایی رخ داد. دوباره تلاش کنید.') {
  const err = error && typeof error === 'object' ? error : {};
  const response = /** @type {{ response?: { data?: unknown; status?: number } }} */ (err).response;
  const status = response?.status;
  const data = response?.data;

  if (status === 402) {
    if (data && typeof data === 'object') {
      const msg = /** @type {{ message?: string }} */ (data).message;
      if (typeof msg === 'string' && msg.trim()) return msg.trim();
    }
    return 'این عملیات با پکیج فعلی سازمان ممکن نیست.';
  }

  if (status === 403) {
    if (data && typeof data === 'object') {
      const msg = /** @type {{ message?: string }} */ (data).message;
      if (typeof msg === 'string' && msg.trim()) {
        const normalized = msg.trim();
        if (/شما عضو این سازمان نیستید/i.test(normalized)) {
          return 'شما عضو این سازمان نیستید.';
        }
        if (/دسترسی ممنوع|forbidden/i.test(normalized)) {
          return 'دسترسی ممنوع — شما مجوز انجام این عملیات را ندارید.';
        }
        return normalized;
      }
    }
    return 'دسترسی ممنوع — شما مجوز انجام این عملیات را ندارید.';
  }

  if (status === 429) {
    if (data && typeof data === 'object') {
      const msg = /** @type {{ message?: string }} */ (data).message;
      if (typeof msg === 'string' && msg.trim()) return msg.trim();
    }
    return 'تعداد درخواست بیش از حد مجاز است. لطفاً کمی بعد دوباره تلاش کنید.';
  }

  if (data && typeof data === 'object') {
    const errors = /** @type {{ errors?: Record<string, string[] | string> }} */ (data).errors;
    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
      const keys = Object.keys(errors);
      if (keys.length) {
        for (const k of keys) {
          const msgs = normalizeValidationMessages(errors[k]);
          if (msgs.length) return msgs[0];
        }
      }
    }

    const msg = /** @type {{ message?: string }} */ (data).message;
    if (typeof msg === 'string' && msg.trim()) {
      const normalized = msg.trim();
      if (isPostTooLargeMessage(normalized)) {
        return postTooLargeUserMessage();
      }
      if (/No query results for model/i.test(normalized)) {
        return 'منبع یافت نشد.';
      }
      if (/<html\b|internal server error|status code 5\d\d/i.test(normalized)) {
        return 'سرویس تبدیل گفتار با خطا پاسخ داد. لطفاً کمی بعد دوباره تلاش کنید.';
      }
      return normalized;
    }
  } else if (typeof data === 'string' && isPostTooLargeMessage(data)) {
    return postTooLargeUserMessage();
  }

  const axiosMsg = /** @type {{ message?: string }} */ (err).message;
  if (typeof axiosMsg === 'string' && axiosMsg.trim() && !axiosMsg.startsWith('Request failed')) {
    if (isPostTooLargeMessage(axiosMsg)) {
      return postTooLargeUserMessage();
    }
    if (isTransportFailureMessage(axiosMsg)) {
      return transportFailureUserMessage();
    }
    if (/No query results for model/i.test(axiosMsg)) {
      return 'منبع یافت نشد.';
    }
    return axiosMsg.trim();
  }

  return fallback;
}

/**
 * @param {unknown} error
 * @returns {{ message: string, reasonCode: string|null, actionPath: string|null }}
 */
export function getApiCapabilityError(error, fallback = 'این عملیات با پکیج فعلی سازمان ممکن نیست.') {
  const err = error && typeof error === 'object' ? error : {};
  const data = /** @type {{ response?: { data?: Record<string, unknown> } }} */ (err).response?.data;
  const inner = data && typeof data.data === 'object' && data.data !== null
    ? /** @type {Record<string, unknown>} */ (data.data)
    : data;
  return {
    message: getApiErrorMessage(error, fallback),
    reasonCode: typeof inner?.reason_code === 'string' ? inner.reason_code : null,
    actionPath: typeof inner?.action_path === 'string' ? inner.action_path : null,
  };
}
